import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck, FaRegEye } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import PageHeading from "../PageHeading";
import BirthRecordForm from "./BirthRecordForm";
import ExportOptions from "../ExportOptions";
import ConfirmActionModal from "../ConfirmDeleteModal";
import TableSkeleton from "../TableSkeleton";
import ViewBirthRecordModal from "./ViewBirthRecordModal";

const API_URL = "https://api.muktihospital.com/api/birth-records";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const BirthRecordModal = ({ isOpen, onClose, onSuccess, editing }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-[95%] sm:max-w-[80%] md:max-w-[1000px] max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-4 right-4 text-xl text-gray-500 hover:text-gray-800"
          onClick={onClose}
        >
          ✕
        </button>
        <BirthRecordForm
          initialData={editing}
          onSuccess={onSuccess}
          onCancel={onClose}
        />
      </div>
    </div>
  );
};

const BirthRecordManager = () => {
  const [records, setRecords] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const itemsPerPage = 5;

  // Skeleton columns configuration
  const skeletonColumns = [
    {
      width: 40,
      height: 16,
      isCircle: false,
      className: "pl-3 print-hidden-in-print",
    }, // Checkbox
    { width: 40, height: 16, className: "pl-3" }, // SL
    { width: 80, height: 16, className: "pl-3" }, // Child ID
    { width: 150, height: 16, className: "pl-3" }, // Child Name
    { width: 80, height: 16, className: "pl-3" }, // Gender
    { width: 80, height: 16, className: "pl-3" }, // Weight
    { width: 100, height: 16, className: "pl-3" }, // Birth Date
    { width: 150, height: 16, className: "pl-3" }, // Mother Name
    { width: 150, height: 16, className: "pl-3" }, // Father Name
    { width: 100, height: 16, className: "pl-3" }, // Phone
    { width: 100, height: 16, className: "pl-3 print-hidden-in-print" }, // Actions
  ];

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
      });
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      setRecords(list);
      setFiltered(list);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load birth records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    const term = search.toLowerCase();
    const result = records.filter((item) =>
      item.childName?.toLowerCase().includes(term)
    );
    setFiltered(result);
    setCurrentPage(1);
    setSelectedRows([]);
  }, [search, records]);

  const filteredList = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredList.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (recordId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(recordId)
        ? prevSelectedRows.filter((id) => id !== recordId)
        : [...prevSelectedRows, recordId]
    );
  };

  const handleDelete = async (recordIds) => {
    setLoading(true);
    try {
      if (Array.isArray(recordIds)) {
        await Promise.all(
          recordIds.map((id) =>
            axios.delete(`${API_URL}/${id}`, {
              headers: { "x-api-key": API_KEY },
            })
          )
        );
        toast.success(`${recordIds.length} record(s) deleted successfully.`);
      } else {
        await axios.delete(`${API_URL}/${recordIds}`, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Birth record deleted successfully.");
      }
      setSelectedRows([]);
      fetchRecords();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete birth record(s).");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (data = null) => {
    setEditing(data);
    setModalOpen(true);
  };

  const openViewModal = (record) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedRecord(null);
  };

  const handleSuccess = () => {
    toast.success(
      editing
        ? "Birth record updated successfully."
        : "Birth record added successfully."
    );
    closeModal();
    fetchRecords();
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "SL",
    "Child ID",
    "Child Name",
    "Gender",
    "Weight",
    "Birth Date",
    "Mother Name",
    "Father Name",
    "Phone",
  ];

  const dataMapper = (item, index) =>
    [
      (currentPage - 1) * itemsPerPage + records.indexOf(item) + 1,
      item.id || "",
      item.childName || "",
      item.gender || "",
      item.weight || "",
      item.birthDate ? new Date(item.birthDate).toLocaleDateString() : "",
      item.motherName || "",
      item.fatherName || "",
      item.phone || "",
    ].join("\t");

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Birth Records"},
    { label: "Birth Record List" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Birth Record List" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Birth Record List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                disabled={loading}
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by child name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => openModal()}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Birth Record
              </button>
              <ExportOptions
                data={filtered}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Birth Record Reports"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={
                          selectedRows.length === filteredList.length &&
                          filteredList.length > 0
                        }
                        disabled={loading}
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  {headers.map((header, i) => (
                    <th
                      key={i}
                      className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                    >
                      {header}
                    </th>
                  ))}
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={skeletonColumns}
                  />
                ) : filteredList.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={headers.length + 2}
                      className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                    >
                      No birth records found.
                    </td>
                  </tr>
                ) : (
                  filteredList.map((rec, i) => (
                    <tr key={rec.id} className="border-b hover:bg-gray-50">
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                        <div className="relative left-3 -top-[4px]">
                          <input
                            type="checkbox"
                            id={`record-${i}`}
                            className="hidden peer print-hidden"
                            checked={selectedRows.includes(rec.id)}
                            onChange={() => handleRowSelect(rec.id)}
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`record-${i}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {(currentPage - 1) * itemsPerPage + i + 1}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.id || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.childName || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.gender || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.weight || "N/A"} Kg
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.birthDate
                          ? new Date(rec.birthDate).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.motherName || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.fatherName || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {rec.phone || "N/A"}
                      </td>
                      <td className="p-3 text-left space-x-2 print-hidden-in-print flex shrink-0">
                        <button
                          onClick={() => openViewModal(rec)}
                          className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          disabled={loading}
                          title="View"
                        >
                          <FaRegEye />
                        </button>
                        <button
                          onClick={() => openModal(rec)}
                          className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          disabled={loading}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRows([rec.id]);
                            setDeleteModalOpen(true);
                          }}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          disabled={loading}
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-5 text-end">
              <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                {/* Previous Button */}
                <li>
                  <button
                    className="table-pagination-item disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <MdKeyboardArrowLeft />
                  </button>
                </li>

                {/* Pagination Items */}
                {totalPages <= 10
                  ? // Show all pages if totalPages <= 10
                    [...Array(totalPages)].map((_, i) => (
                      <li key={i}>
                        <button
                          className={`table-pagination-item ${
                            currentPage === i + 1
                              ? "bg-M-primary-color text-white"
                              : ""
                          }`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))
                  : // Show first 5, ellipsis, and last 3 if totalPages > 10
                    (() => {
                      const pages = [];
                      const firstPages = 5;
                      const lastPages = 3;

                      // Always show first 5 pages
                      for (
                        let i = 1;
                        i <= Math.min(firstPages, totalPages);
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      // Add ellipsis if there are pages between first 5 and last 3
                      if (totalPages > firstPages + lastPages) {
                        pages.push(
                          <li key="ellipsis" className="table-pagination-item">
                            ...
                          </li>
                        );
                      }

                      // Show last 3 pages
                      for (
                        let i = Math.max(
                          firstPages + 1,
                          totalPages - lastPages + 1
                        );
                        i <= totalPages;
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      return pages;
                    })()}

                {/* Next Button */}
                <li>
                  <button
                    className="table-pagination-item"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <MdKeyboardArrowRight />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      <BirthRecordModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSuccess={handleSuccess}
        editing={editing}
      />
      <ViewBirthRecordModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        record={selectedRecord}
        onEdit={openModal} // This should be a function
        onDelete={(ids) => {
          setSelectedRows(ids);
          setDeleteModalOpen(true);
        }}
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="birth record"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default BirthRecordManager;